import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ls from 'localstorage-slim';
import { Pagination } from 'rsuite';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Icon } from '@rsuite/icons';
import { FaRegTimesCircle, FaRegCheckCircle, FaRegClock, FaPlus } from 'react-icons/fa';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';

import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function InvoicePackages() {
  const [documentPackages, setDocumentPackages] = useState([]);
  const [documentPackagesLength, setDocumentPackagesLength] = useState(0);
  const [metaData, setMetaData] = useState({});
  const [activePage, setActivePage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [documentStatuses, setDocumentStatuses] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [type, setType] = useState('');
  const [format, setFormat] = useState('');
  const [queryParameters, setQueryParameters] = useState({});

  const { t, i18n } = useTranslation();

  const subDomain = ls.get('subDomain');

  const handleType = event => {
    setType(event.target.value);
  };

  const handleFormat = event => {
    setFormat(event.target.value);
  };

  const handleDateFrom = event => {
    const dateFrom = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, dateFrom });
  };

  const handleChangeInputType = (event, inputType) => {
    if (!event.target.value) event.target.type = inputType;
  };

  const handleDateTo = event => {
    const dateTo = new Date(event.target.value).toISOString();
    setQueryParameters({ ...queryParameters, dateTo });
  };

  const handleDocumentTypes = event => {
    let documentTypeNames = [];
    for (const documentType of documentTypes) {
      documentTypeNames.push(documentType.value);
    }
    setQueryParameters({ ...queryParameters, documentTypeNames });
  };

  const handleStatuses = event => {
    let statuses = [];
    for (const documentStatus of documentStatuses) {
      statuses.push(documentStatus.value);
    }

    setQueryParameters({ ...queryParameters, statuses });
  };

  const handleRequestPackage = () => {
    const requestPackageToast = toast.loading('Please wait...');

    axios
      .post(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/request_document_package`,
        {
          type,
          format,
          queryParameters,
        },
        {
          params: { accessToken: ls.get('ETAToken'), taxpayer: ls.get('taxpayer_id') },
        }
      )
      .then(response => {
        setDocumentPackagesLength(documentPackages.length + 1);
        toast.update(requestPackageToast, {
          render: `${response.data.message}`,
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      })
      .catch(error => {
        if (error.response.data.error.details) {
          for (let err of error.response.data.error.details) {
            toast.update(requestPackageToast, {
              render: `${err.message}`,
              type: 'error',
              isLoading: false,
              autoClose: 1000,
              theme: 'colored',
            });
          }
        }
      });
  };

  const handlePackageDownload = rid => {
    let downloadPackageToast = toast.loading('Please wait...');

    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/download_document_package`, {
        responseType: 'arraybuffer',
        params: { accessToken: ls.get('ETAToken'), rid, taxpayer: ls.get('taxpayer_id') },
      })
      .then(async response => {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: `${rid}.zip`,
            types: [
              {
                description: 'ZIP',
                accept: {
                  'application/octet-stream': ['.zip'],
                },
              },
            ],
          });

          // Create a FileSystemWritableFileStream to write
          const writable = await handle.createWritable();

          // Write the contents of the file to the stream
          await writable.write(new Blob([response.data]));

          // Close the file and write the contents to disk
          await writable.close();

          toast.update(downloadPackageToast, {
            render: 'Document Package downloaded successfully',
            type: 'success',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } catch (err) {
          toast.update(downloadPackageToast, {
            render: `${err.name}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      })
      .catch(error => {
        toast.update(downloadPackageToast, {
          render: `${error}`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  useEffect(() => {
    async function fetchData() {
      const documentPackageToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/get_package_requests`,
          {
            params: {
              accessToken: ls.get('ETAToken'),
              pageNumber: activePage,
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setDocumentPackages(response.data.result);
        setMetaData(response.data.metadata);
        setIsLoading(false);
        toast.update(documentPackageToast, {
          render: 'Document Packages retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(documentPackageToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(documentPackageToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(documentPackageToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, [activePage, documentPackagesLength]);

  return isLoading ? (
    <div style={{ height: '77vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div
        className='modal fade'
        id='staticBackdrop'
        data-bs-backdrop='static'
        data-bs-keyboard='false'
        tabIndex='-1'
        aria-labelledby='staticBackdropLabel'
        aria-hidden='true'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title' id='staticBackdropLabel'>
                {t('documentPackage.modal.title')}
              </h5>
              <button
                type='button'
                className='btn-close'
                data-bs-dismiss='modal'
                aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <form
                className={`${i18n.language === 'en' ? 'pe-3' : 'ps-3'} text-center`}
                dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
                <div className='row mt-2 mb-3'>
                  <span
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } fs-4 text-decoration-underline`}
                    style={{ textUnderlineOffset: '10px' }}>
                    <span>{t('documentPackage.modal.packageDetails.title')}</span>
                  </span>
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='type'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.packageDetails.type')}
                  </label>
                  <select
                    className='col-8 py-1'
                    aria-label='Default select example'
                    id='type'
                    name='type'
                    onChange={handleType}>
                    <option defaultValue={''}></option>
                    <option value='Full'>{t('documentPackage.modal.packageDetails.full')}</option>
                    <option value='Summary'>
                      {t('documentPackage.modal.packageDetails.summary')}
                    </option>
                  </select>
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='format'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.packageDetails.format')}
                  </label>
                  <select
                    className='col-8 py-1'
                    aria-label='Default select example'
                    id='format'
                    name='format'
                    onChange={handleFormat}>
                    <option defaultValue={''}></option>
                    <option value='CSV'>CSV</option>
                    <option value='XML'>XML</option>
                    <option value='JSON'>JSON</option>
                  </select>
                </div>
                <div className='row mt-2 mb-3'>
                  <span
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } fs-4 text-decoration-underline`}
                    style={{ textUnderlineOffset: '10px' }}>
                    <span>{t('documentPackage.modal.inquiryDetails.title')}</span>
                  </span>
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='dateFrom'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block text-nowrap`}>
                    {t('documentPackage.modal.inquiryDetails.issueDate')}
                  </label>
                  <input
                    type='text'
                    className={`${i18n.language === 'en' ? 'me-1' : 'ms-1'} col`}
                    id='dateFrom'
                    name='dateFrom'
                    onChange={handleDateFrom}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    placeholder={t('document.from')}
                  />
                  <input
                    type='text'
                    className='col'
                    id='dateTo'
                    name='dateTo'
                    onChange={handleDateTo}
                    onFocus={event => handleChangeInputType(event, 'datetime-local')}
                    onBlur={event => handleChangeInputType(event, 'text')}
                    placeholder={t('document.to')}
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='documentType'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block text-nowrap py-2`}>
                    {t('documentPackage.modal.inquiryDetails.type')}
                  </label>
                  <MultiSelect
                    options={[
                      { label: 'Invoice', value: 'I' },
                      { label: 'Debit Note', value: 'D' },
                      { label: 'Credit Note', value: 'C' },
                    ]}
                    value={documentTypes}
                    onChange={setDocumentTypes}
                    onMenuToggle={handleDocumentTypes}
                    labelledBy='Select'
                    className='col-8'
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='exampleInputReason'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block py-2`}>
                    {t('documentPackage.modal.inquiryDetails.status')}
                  </label>
                  <MultiSelect
                    options={[
                      { label: 'Valid', value: 'Valid' },
                      { label: 'Invalid', value: 'Invalid' },
                      { label: 'Cancelled', value: 'Cancelled' },
                      { label: 'Rejected', value: 'Rejected' },
                    ]}
                    value={documentStatuses}
                    onChange={setDocumentStatuses}
                    onMenuToggle={handleStatuses}
                    labelledBy='Select'
                    className='col-8'
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='exampleInputReason'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : ' text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.inquiryDetails.secondTaxpayer')}
                  </label>
                  <select
                    className={`${i18n.language === 'en' ? 'me-2' : 'ms-2'} col`}
                    aria-label='Default select example'
                    id='type'
                    name='type'>
                    <option value='Any'>Any</option>
                    <option value='Business'>Business</option>
                    <option value='Person'>Person</option>
                    <option value='Foreign'>Foreign</option>
                  </select>
                  <input
                    type='text'
                    className='col'
                    id='exampleInputReason'
                    // onChange={handleChange}
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='exampleInputReason'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.inquiryDetails.issuerBranch')}
                  </label>
                  <input
                    type='text'
                    className='col-8'
                    id='exampleInputReason'
                    // onChange={handleChange}
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='exampleInputReason'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.inquiryDetails.internalCodes')}
                  </label>
                  <input
                    type='text'
                    className='col-8'
                    id='exampleInputReason'
                    // onChange={handleChange}
                  />
                </div>
                <div className='row mb-3'>
                  <label
                    htmlFor='exampleInputReason'
                    className={`${
                      i18n.language === 'en' ? 'text-start' : 'text-end'
                    } col-4 d-inline-block`}>
                    {t('documentPackage.modal.inquiryDetails.lineItem')}
                  </label>
                  <select
                    className={`${i18n.language === 'en' ? 'me-1' : 'ms-1'} col`}
                    aria-label='Default select example'
                    id='type'
                    name='type'>
                    <option value='GS1'>GS1</option>
                    <option value='EGS'>EGS</option>
                  </select>
                  <input
                    type='text'
                    className='col-6'
                    id='exampleInputReason'
                    // onChange={handleChange}
                  />
                </div>
              </form>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>
                {t('documentPackage.modal.close')}
              </button>
              <button type='button' className='btn btn-danger' onClick={handleRequestPackage}>
                {t('documentPackage.modal.request')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='container-fluid'>
        <div className='shadow m-3 p-2'>
          <div className={'row my-3 mx-2'}>
            <span
              className={`${
                i18n.language === 'en' ? 'text-start order-1 me-5' : 'text-end order-2 ms-5'
              } col-2 fs-4 text-decoration-underline`}
              style={{ textUnderlineOffset: '10px' }}>
              <span>{t('documentPackage.title')}</span>
            </span>
            <span
              className={`${
                i18n.language === 'en' ? 'order-2 text-end ms-4' : 'order-1 text-start me-4'
              } col-9 btn py-1`}
              data-bs-toggle='modal'
              data-bs-target='#staticBackdrop'>
              <Icon as={FaPlus} color='Blue' className='me-1' /> {t('documentPackage.button')}
            </span>
          </div>
          <div className='row justify-content-center mx-2 text-center'>
            <table className='table col-12 mt-2' dir={`${i18n.language === 'ar' && 'rtl'}`}>
              <thead
                style={{
                  verticalAlign: 'middle',
                }}>
                <tr>
                  <th scope='col'>{t('documentPackage.table.id')}</th>
                  <th scope='col'>{t('documentPackage.table.date')}</th>
                  <th scope='col'>{t('documentPackage.table.format')}</th>
                  <th scope='col'>{t('documentPackage.table.requestedBy')}</th>
                  <th scope='col'>{t('documentPackage.table.status')}</th>
                  <th scope='col'>{t('documentPackage.table.deletion')}</th>
                  <th scope='col'></th>
                </tr>
              </thead>
              <tbody>
                {documentPackages.length !== 0 &&
                  documentPackages.map(documentPackage => (
                    <tr key={documentPackage.packageId}>
                      <td>
                        <div className='fw-bold py-2'>
                          <Link to={`../../documents/${documentPackage.packageId}`}>
                            {documentPackage.packageId}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <span className='d-block fw-bold'>
                          {moment(documentPackage.submissionDate).format('DD/MM/YYYY')}
                        </span>
                        <span className='d-block' dir='ltr'>
                          {moment(documentPackage.submissionDate).format('h:mm a')}
                        </span>
                      </td>
                      <td>
                        <span className='d-block fw-bold'>
                          {documentPackage.queryParams.documentFormat}
                        </span>
                        <span className='d-block'>
                          {documentPackage.type === 2 ? 'Summary' : 'Full'}
                        </span>
                      </td>
                      <td>
                        <div className='py-2'>{documentPackage.requestorTaxpayerName}</div>
                      </td>
                      <td>
                        <div className='py-2'>
                          {documentPackage.status === 1 ? (
                            <span>
                              <Icon as={FaRegClock} color='grey' /> In progress
                            </span>
                          ) : documentPackage.status === 2 ? (
                            <span>
                              <Icon as={FaRegCheckCircle} color='green' /> Completed
                            </span>
                          ) : (
                            <span>
                              <Icon as={FaRegTimesCircle} color='red' /> Invalid
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className='d-block fw-bold'>
                          {documentPackage.deletionDate &&
                            moment(documentPackage.deletionDate).format('DD/MM/YYYY')}
                        </span>
                        <span className='d-block'>
                          {documentPackage.deletionDate &&
                            moment(documentPackage.deletionDate).format('h:mm a')}
                        </span>
                      </td>
                      <td>
                        {documentPackage.status === 2 && (
                          <div
                            className='btn btn-primary text-white'
                            onClick={() => {
                              handlePackageDownload(documentPackage.packageId);
                            }}>
                            Download
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {documentPackages.length === 0 && (
              <div className='d-flex justify-content-center'>
                <span>{t('document.table.noRecords')}</span>
              </div>
            )}

            <div className='row'>
              <Pagination
                prev
                next
                total={metaData['totalCount']}
                limit={10}
                activePage={activePage}
                onChangePage={setActivePage}
                className='my-2 justify-content-center'
                size='md'
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InvoicePackages;
